import { usePostHog } from '@posthog/react';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getSignedUpPatient, useOnboardingStore } from '../store/onboardingStore';
import { ONBOARDING_PROCESS_TYPE_BY_PATH, ONBOARDING_SHOWN_EVENT_BY_PATH } from './onboardingShownTracking';

const BILLING_TYPE_PATHS = new Set([
  '/webAppOnboarding/insurance-invoice-explain',
  '/webAppOnboarding/insurance-blocked-free-process-overview',
  '/webAppOnboarding/insurance-blocked-practice-fee-process-overview',
  '/webAppOnboarding/insurance-blocked-temp-process-overview',
  '/webAppOnboarding/insurance-abtretung-process-overview',
  '/webAppOnboarding/insurance-bnpl-process-overview',
  '/webAppOnboarding/insurance-without-abtretung-physiofit-process-overview',
  '/webAppOnboarding/insurance-without-abtretung-patient-process-overview',
  '/webAppOnboarding/insurance-bnpl-checkout',
  '/webAppOnboarding/insurance-bnpl-payment',
  '/webAppOnboarding/course-not-certified-process-overview',
  '/webAppOnboarding/install',
]);

export const useTrackOnboardingStepShown = () => {
  const posthog = usePostHog();
  const location = useLocation();
  const lastPath = useRef<string | null>(null);
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);

  useEffect(() => {
    const path = location.pathname.replace(/\/$/, '') || '/webAppOnboarding';
    if (path === lastPath.current) return;
    const event = ONBOARDING_SHOWN_EVENT_BY_PATH[path];
    if (!event) return;

    const needsBillingType = BILLING_TYPE_PATHS.has(path);
    // Defer firing on billingType-dependent screens until the signed-up patient is loaded.
    // Otherwise the shown event is sent before `billingType` is available and the `lastPath`
    // guard would block a corrected re-send (PF-4982). The effect re-runs when `signedUpPatient`
    // arrives (it is a dependency), so the event then fires once with the resolved value.
    if (needsBillingType && !signedUpPatient) return;

    lastPath.current = path;
    const processType = ONBOARDING_PROCESS_TYPE_BY_PATH[path];
    const billingType = needsBillingType
      ? signedUpPatient?.webAppOnboarding?.insuranceInfo?.billingType
      : undefined;
    const props = processType || billingType ? { ...(processType ? { processType } : {}), ...(billingType ? { billingType } : {}) } : undefined;
    posthog.capture(event, props);
  }, [location.pathname, posthog, signedUpPatient]);
};
